import { useState, useEffect } from 'react'
import { Eye, EyeOff, Copy, Check, MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from 'sonner'
import type { ClaveCliente } from '@/hooks/useVault'

interface ClaveCardProps {
  clave: ClaveCliente
  onEditar: (clave: ClaveCliente) => void
  onEliminar: (id: string) => void
  /** Sin permiso para cambiar claves, la tarjeta no muestra el menú de editar y eliminar. */
  editable?: boolean
}

export function ClaveCard({ clave, onEditar, onEliminar, editable = true }: ClaveCardProps) {
  const [revelada, setRevelada] = useState(false)
  const [copiado, setCopiado] = useState<string | null>(null)
  const valor = clave.clave

  useEffect(() => {
    if (!copiado) return
    const timer = setTimeout(() => setCopiado(null), 2000)
    return () => clearTimeout(timer)
  }, [copiado])

  const handleCopy = async (value: string, field: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopiado(field)
      toast.success('Copiado al portapapeles')
    } catch {
      toast.error('Error al copiar')
    }
  }

  return (
    <div className="rounded-lg border p-4 space-y-2">
      <div className="flex items-center justify-between">
        <span className="font-medium text-sm">{clave.organismo}</span>
        {editable && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEditar(clave)}>
              <Pencil className="h-4 w-4 mr-2" />
              Editar
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive"
              onClick={() => onEliminar(clave.id)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        )}
      </div>

      {clave.usuario && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Usuario</span>
          <button
            onClick={() => handleCopy(clave.usuario!, 'usuario')}
            className="flex items-center gap-1.5 hover:text-primary transition-colors font-mono"
          >
            {clave.usuario}
            {copiado === 'usuario' ? (
              <Check className="h-3.5 w-3.5 text-emerald-500" />
            ) : (
              <Copy className="h-3.5 w-3.5 opacity-50" />
            )}
          </button>
        </div>
      )}

      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">Clave</span>
        {valor === null ? (
          <span className="text-destructive text-right">
            No se pudo descifrar esta clave. Avisá al administrador antes de editarla.
          </span>
        ) : (
          <div className="flex items-center gap-1">
            <span className="font-mono">
              {revelada ? valor : '••••••••'}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              aria-label={revelada ? 'Ocultar clave' : 'Mostrar clave'}
              onClick={() => setRevelada(!revelada)}
            >
              {revelada ? (
                <EyeOff className="h-3.5 w-3.5" />
              ) : (
                <Eye className="h-3.5 w-3.5" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              aria-label="Copiar clave"
              onClick={() => handleCopy(valor, 'clave')}
            >
              {copiado === 'clave' ? (
                <Check className="h-3.5 w-3.5 text-emerald-500" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        )}
      </div>

      {clave.notas && (
        <p className="text-xs text-muted-foreground mt-1">{clave.notas}</p>
      )}
    </div>
  )
}
